import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";

const SPECIES = [
  { value: "dog",          label: "Dog",          sub: "Canis lupus familiaris" },
  { value: "cat",          label: "Cat",          sub: "Felis catus"            },
  { value: "small-animal", label: "Small Animal", sub: "Rabbit, guinea pig & more" },
];

interface SpeciesSelectProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

export function SpeciesSelect({ value, onChange, required }: SpeciesSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, []);

  const selected = SPECIES.find(s => s.value === value);

  return (
    <div ref={ref} className="relative flex flex-col gap-2">
      <label className="font-grotesk text-[10px] uppercase tracking-widest opacity-50">
        Species{required ? " *" : ""}
      </label>

      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`
          flex items-center justify-between w-full
          border-b py-3 text-left
          font-sans text-sm
          focus:outline-none transition-colors duration-300
          ${open ? "border-foreground/60" : "border-foreground/20"}
        `}
      >
        <span className={selected ? "opacity-90" : "opacity-25 font-sans text-sm"}>
          {selected ? selected.label : "Select species"}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="opacity-35 ml-2 shrink-0"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scaleY: 0.92 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -6, scaleY: 0.94 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            style={{ originY: 0 }}
            className="absolute top-[calc(100%+2px)] left-0 right-0 z-50
                       bg-background border border-foreground/10
                       shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)]
                       overflow-hidden"
          >
            {SPECIES.map((s, i) => {
              const active = value === s.value;
              return (
                <motion.li
                  key={s.value}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.22, delay: i * 0.055, ease: [0.16, 1, 0.3, 1] }}
                  className={`border-b border-foreground/5 last:border-0`}
                >
                  <button
                    type="button"
                    onClick={() => { onChange(s.value); setOpen(false); }}
                    className={`
                      w-full flex items-center justify-between
                      px-5 py-4 text-left group
                      transition-colors duration-200
                      hover:bg-foreground/[0.03]
                    `}
                  >
                    <span className="flex flex-col gap-0.5">
                      <span className={`font-sans text-sm transition-colors duration-200 ${active ? "text-primary" : "text-foreground"}`}>
                        {s.label}
                      </span>
                      <span className="font-grotesk text-[9px] uppercase tracking-widest opacity-30 italic">
                        {s.sub}
                      </span>
                    </span>
                    <AnimatePresence>
                      {active && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.6 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ duration: 0.2 }}
                          className="text-primary"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
